import React, { useState } from "react";
import { Box, InputAdornment, Tooltip, Typography } from "@mui/material";
import { Add as AddIcon, Search as SearchIcon } from "@mui/icons-material";
import {  SortAsc, SortDesc} from "lucide-react";

// Components
import SeriesItem from "./common/SeriesItem";
import EpisodePanelSection from "./EpisodePanelSection";

// Utils
import { RunsFilter, RunsSlice } from "./common/runsQuery";

// Types
import {
  SeriesSerializer,
  SeriesType,
  ShareRunMapData,
} from "./types/MeGenTypes";

// Styles
import {
  CreateButton,
  EmptyState,
  ScrollableList,
  SearchInput,
  SortButton,
  ToolbarRow,
} from "./styles/ProjectDetail";

// ─── Styled ───────────────────────────────────────────────

import { styled } from "@mui/material/styles";

const PlaylistRoot = styled(Box)({
  display: "flex",
  flex: 1,
  overflow: "hidden",
  gap: 0,
});

const SeriesColumn = styled(Box)<{ hasEpisodePanel: boolean }>(({ hasEpisodePanel }) => ({
  flex: hasEpisodePanel ? 4 : 1,
  minWidth: 0,
  display: "flex",
  flexDirection: "column",
  overflow: "hidden",
  paddingRight: hasEpisodePanel ? "1rem" : "0",
  transition: "flex 0.3s ease, padding 0.3s ease",
}));

// ─── Props ────────────────────────────────────────────────

type CardProjectDetailPlaylistProps = {
  seriesList: SeriesSerializer[];
  /** episode ของ series ที่เลือก ค้น/แบ่งหน้ามาจาก server — `seriesId` ใน slice คือ series ที่เปิดแผงอยู่ */
  runsPlaylist: RunsSlice;
  onSearchPlaylistRuns?: (filter: RunsFilter, mode?: "replace" | "append") => void;
  // Series
  onCreateFromScript?: () => void;
  onCreateFromWebsite?: () => void;
  onViewSeries?: (series: SeriesSerializer) => void;
  onEditSeries?: (series: SeriesSerializer) => void;
  onDeleteSeries?: (series: SeriesSerializer) => void;
  onShareSeries?: (series: SeriesSerializer) => void;
  // Episodes
  onCreateVdoFromWebsite?: (seriesId?: number) => void;
  onCreateVdoFromScript?: (seriesId?: number) => void;
  onCreateVdoFromStory?: (seriesId?: number) => void;
  onSelectEpisode?: (episodeId: string, runType?: SeriesType) => void;
  onRestartEpisode?: (episodeId: string) => void;
  onDuplicateEpisode?: (episodeId: string) => void;
  onDeleteEpisode?: (episodeId: string) => void;
  onShareEpisode?: (shareData: ShareRunMapData) => void;
};

// ─── Component ────────────────────────────────────────────

const CardProjectDetailPlaylist: React.FC<CardProjectDetailPlaylistProps> = (props) => {

  const [searchQuery, setSearchQuery] = useState("");
  const [sortOrder, setSortOrder] = useState<"newest" | "oldest">("newest");

  // Filter + Sort series
  const filteredSeries = props.seriesList
    .filter((s) => {
      const q = searchQuery.toLowerCase();
      return s.name.toLowerCase().includes(q) || s.series_type.toLowerCase().includes(q);
    })
    .sort((a, b) => {
      const idA = a.id || 0;
      const idB = b.id || 0;
      return sortOrder === "newest" ? idB - idA : idA - idB;
    });

  // series ที่เลือกอยู่ = seriesId ใน slice (อยู่ใน state กลาง ⇒ สลับแท็บไปมาแล้วแผงยังเปิดอยู่)
  const selectedSeriesId = props.runsPlaylist.seriesId ?? null;
  const selectedSeriesData = props.seriesList.find((s) => s.id === selectedSeriesId);

  // เปิดแผงของ series: เริ่มหน้าแรก ล้างคำค้น คงลำดับที่ตั้งไว้
  // null = ปิดแผง (handler ล้าง slice โดยไม่ยิง request)
  const selectSeries = (seriesId: number | null) =>
    props.onSearchPlaylistRuns?.({ q: "", sort: props.runsPlaylist.sort, seriesId }, "replace");

  // Create episode for selected series
  const handleCreateEpisode = () => {
    if (!selectedSeriesData) return;
    switch (selectedSeriesData.series_type) {
      case "rss_news":
        props.onCreateVdoFromWebsite?.(selectedSeriesData.id);
        break;
      case "script2vdo":
        props.onCreateVdoFromScript?.(selectedSeriesData.id);
        break;
      case "story2scene":
        props.onCreateVdoFromStory?.(selectedSeriesData.id);
        break;
    }
  };

  const handleSelectSeries = (series: SeriesSerializer) => {
    if (series.id == null) return; // เดิมใช้ `series.id!` — id ว่างเคยกลายเป็น "ไม่ได้เลือก" เงียบ ๆ
    selectSeries(selectedSeriesId === series.id ? null : series.id);
  };

  return (
    <PlaylistRoot id="megen-playlist-root">
      {/* Series Column */}
      <SeriesColumn id="megen-playlist-series-column" hasEpisodePanel={selectedSeriesId != null}>
        {/* Toolbar */}
        <ToolbarRow id="megen-playlist-toolbar">
          <SearchInput
            id="megen-playlist-search-input"
            placeholder="Search playlist..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon sx={{ color: "#94a3b8", fontSize: "1.25rem" }} />
                </InputAdornment>
              ),
            }}
          />

          <Tooltip title={sortOrder === "newest" ? "Newest First" : "Oldest First"}>
            <SortButton id="megen-playlist-sort-btn" onClick={() => setSortOrder((prev) => (prev === "newest" ? "oldest" : "newest"))}>
              {sortOrder === "newest" ? <SortDesc size={18} /> : <SortAsc size={18} />}
            </SortButton>
          </Tooltip>

          <CreateButton
            id="megen-playlist-create-btn"
            startIcon={<AddIcon />}
            onClick={() => props.onCreateFromWebsite?.()}
          >
            New Playlist
          </CreateButton>
        </ToolbarRow>

        {/* Series List */}
        {filteredSeries.length === 0 ? (
          <EmptyState id="megen-playlist-empty-state">
            {searchQuery ? (
              <Typography sx={{ fontSize: "1rem" }}>
                No playlist found matching "{searchQuery}".
              </Typography>
            ) : (
              <>
                <Typography sx={{ fontSize: "1.1rem", marginBottom: "0.5rem" }}>
                  ยังไม่มี Playlist ที่คุณสร้างไว้
                </Typography>
                <Typography sx={{ fontSize: "0.9rem", color: "#cbd5e1" }}>
                  กด New Playlist เพื่อเริ่มต้น
                </Typography>
              </>
            )}
          </EmptyState>
        ) : (
          <ScrollableList id="megen-playlist-series-list">
            {filteredSeries.map((series) => (
              <SeriesItem
                key={`series-${series.id}`}
                series={series}
                isActive={selectedSeriesId === series.id}
                onView={props.onViewSeries}
                onSelect={() => handleSelectSeries(series)}
                onEdit={props.onEditSeries}
                onDelete={props.onDeleteSeries}
                onShare={props.onShareSeries}
              />
            ))}
          </ScrollableList>
        )}
      </SeriesColumn>

      {/* Episode Panel */}
      <EpisodePanelSection
        visible={selectedSeriesId != null}
        selectedSeriesData={selectedSeriesData}
        runs={props.runsPlaylist}
        onSearchRuns={props.onSearchPlaylistRuns}
        onBack={() => selectSeries(null)}
        onCreateEpisode={handleCreateEpisode}
        onSelectEpisode={props.onSelectEpisode}
        onRestartEpisode={props.onRestartEpisode}
        onDuplicateEpisode={props.onDuplicateEpisode}
        onDeleteEpisode={props.onDeleteEpisode}
        onShareEpisode={props.onShareEpisode}
      />
    </PlaylistRoot>
  );
};

export default CardProjectDetailPlaylist;
